// @ts-nocheck
/**
 * @file Entry point for Custom Editor created using TipTap
 *
 */

import React, {
  useRef,
  forwardRef,
  useImperativeHandle,
  useCallback,
} from 'react';
import { Stack, useTheme, Box } from '@mui/material';
import { EditorContent, useEditor } from '@tiptap/react';
import PropTypes from 'prop-types';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '../../const/CommonConst';

import SolyticsEditorToolbar from './SolyticsEditorToolbar';
import { defaultExtensions, readModeExtensions } from '.';
import {
  SolyticsEditorProvider,
  useSolyticsEditorContext,
} from './context/SolyticsEditorContext';

/**
 * @typedef SolyticsEditorProps
 * @property {string | JSON} content
 * @property {boolean} editable
 * @property {string[]} [hiddenToolbarItems = []]
 * @property {import('@tiptap/react').UseEditorOptions} [useEditorOptions = {}]
 * @property {import('@tiptap/react').Extensions} [extraExtensions = []]
 * @property {boolean} [hideToolbar = false]
 * @property {import('./SolyticsEditorToolbar').SolyticsEditorToolbarProps} [SolyticsEditorToolbarProps = {}]
 * @property {boolean} [hideToolbar = false]
 */

/**
 * @typedef SolyticsEditorHandle
 * @property {() => string} getHTML
 */

export const ReadOnlyEditor = ({ content, ariaLabel }) => {
  const { editorRef, useEditorOptions, extraExtensions } =
    useSolyticsEditorContext();

  const editor = useEditor({
    content,
    editable: false,
    extensions: [...readModeExtensions, ...extraExtensions],
    onCreate: ({ editor: e }) => {
      editorRef.current = e;
    },
    ...useEditorOptions,
    editorProps: {
      ...(useEditorOptions?.editorProps || {}),
      attributes: {
        ...(useEditorOptions?.editorProps?.attributes || {}),
        role: 'textbox',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
    },
  });

  return <EditorContent editor={editor} />;
};
ReadOnlyEditor.propTypes = {
  content: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string,
};

const EditableEditor = ({ content, ariaLabel }) => {
  const {
    editorRef,
    useEditorOptions,
    extraExtensions,
    hideToolbar,
    SolyticsEditorToolbarProps,
    showEditorBorder,
  } = useSolyticsEditorContext();
  const theme = useTheme();
  const { onCreate, ...others } = useEditorOptions;
  const editor = useEditor({
    content,
    extensions: [...defaultExtensions, ...extraExtensions],
    onCreate: (props) => {
      // eslint-disable-next-line react/prop-types
      editorRef.current = props.editor;
      if (onCreate) {
        onCreate(props);
      }
    },
    ...others,
    editorProps: {
      ...(others?.editorProps || {}),
      attributes: {
        ...(others?.editorProps?.attributes || {}),
        role: 'textbox',
        ...(ariaLabel ? { 'aria-label': ariaLabel } : {}),
      },
    },
  });
  const handleFocus = useCallback(() => {
    editor?.commands?.focus();
  }, [editor]);

  return (
    <>
      {!hideToolbar && (
        <SolyticsEditorToolbar
          editor={editor}
          {...SolyticsEditorToolbarProps}
        />
      )}
      <Box
        onClick={handleFocus}
        sx={[
          {
            cursor: 'text',
          },
          showEditorBorder
            ? {
                border: `1px solid ${theme?.palette?.other?.editorBorder}`,
              }
            : {
                border: 'none',
              },
          showEditorBorder
            ? {
                borderRadius: '4px',
              }
            : {
                borderRadius: 0,
              },
          showEditorBorder
            ? {
                padding: '8px',
              }
            : {
                padding: 0,
              },
          showEditorBorder && {
            '& .ProseMirror p.is-editor-empty:first-of-type::before': {
              content: 'attr(data-placeholder)',
              color: theme?.palette?.text?.disabled,
              pointerEvents: 'none',
            },
          },
        ]}
      >
        <EditorContent editor={editor} style={{ lineHeight: 1.2 }} />
      </Box>
    </>
  );
};
EditableEditor.propTypes = {
  content: PropTypes.string.isRequired,
  ariaLabel: PropTypes.string,
};

/**
 * @type {import('react').ForwardRefRenderFunction<SolyticsEditorHandle, SolyticsEditorProps & import('@mui/material').StackProps & import('react').RefAttributes>}
 */
const SolyticsEditor = forwardRef(
  (
    {
      content,
      editable,
      hiddenToolbarItems = EMPTY_ARRAY,
      useEditorOptions = EMPTY_OBJECT,
      extraExtensions = EMPTY_ARRAY,
      hideToolbar = false,
      showEditorBorder = false,
      SolyticsEditorToolbarProps = EMPTY_OBJECT,
      ariaLabel = undefined,
      ...props
    },
    ref
  ) => {
    /**
     * @type {import('react').MutableRefObject<import('@tiptap/react').Editor>}
     */
    const editorRef = useRef(null);

    useImperativeHandle(ref, () => {
      return {
        getHTML: () => {
          if (editorRef.current) {
            return editorRef.current.getHTML();
          }
          return content;
        },
        setOptions: (options) => {
          if (editorRef.current) {
            editorRef.current.setOptions(options);
          }
        },
        getText: () => {
          if (editorRef.current) {
            editorRef.current.getText();
          }
        },
      };
    }, []);

    return (
      <SolyticsEditorProvider
        value={{
          editorRef,
          hiddenToolbarItems,
          useEditorOptions,
          extraExtensions,
          hideToolbar,
          SolyticsEditorToolbarProps,
          showEditorBorder,
        }}
      >
        <Stack
          {...props}
          sx={[
            {
              gap: 2,
            },
            ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
          ]}
        >
          {editable && (
            <EditableEditor content={content} ariaLabel={ariaLabel} />
          )}
          {!editable && (
            <ReadOnlyEditor content={content} ariaLabel={ariaLabel} />
          )}
        </Stack>
      </SolyticsEditorProvider>
    );
  }
);
SolyticsEditor.propTypes = {
  content: PropTypes.string.isRequired,
  editable: PropTypes.bool.isRequired,
  hiddenToolbarItems: PropTypes.oneOfType([PropTypes.array]).isRequired,
  extraExtensions: PropTypes.oneOfType([PropTypes.array]).isRequired,
  useEditorOptions: PropTypes.oneOfType([PropTypes.object]).isRequired,
  hideToolbar: PropTypes.bool.isRequired,
  SolyticsEditorToolbarProps: PropTypes.oneOfType([PropTypes.object])
    .isRequired,
  showEditorBorder: PropTypes.bool,
  ariaLabel: PropTypes.string,
  sx: PropTypes.oneOfType([PropTypes.object, PropTypes.array, PropTypes.func]),
};

export default SolyticsEditor;
